import React, { useContext } from 'react';
import clsx from 'clsx';

import { WindowSizeContext } from 'contexts/WindowSizeContext';
import Typography from 'components/ui/Typography';

import css from './Tile.module.scss';

interface HeaderProps extends React.PropsWithChildren {
    counter?: string | number;
    more?: React.ReactNode;
    divider?: boolean;
    className?: string;
    beforeContent?: React.ReactNode;
    modalCloseButtonMobile?: React.ReactNode;
    isMobileFullscreen?: boolean;
}

interface TileProps extends React.PropsWithChildren {
    extraPadding?: number;
    margin?: boolean;
    className?: string;
    isMobileFullscreen?: boolean;
    fullHeight?: boolean;
    flexOrder?: number;
}

const Tile: React.FC<TileProps> & {
    Item: React.FC<{ className?: string; children: React.ReactNode }>;
} & {
    Header: React.FC<HeaderProps>;
} = ({
    children,
    extraPadding,
    margin = true,
    isMobileFullscreen = false,
    fullHeight = false,
    className,
    flexOrder,
}) => {
    const { isMobile, visibleHeight } = useContext(WindowSizeContext);

    // Exclude height of navigation controls, etc. from viewport height
    const height = isMobile && isMobileFullscreen ? visibleHeight : undefined;

    return (
        <div
            className={clsx(
                css.tile,
                extraPadding && css[`extraPadding-${extraPadding}`],
                margin && css.margin,
                isMobileFullscreen && isMobile && css.mobileFullscreen,
                fullHeight && css.fullHeight,
                className && className,
            )}
            data-asdfaf-asdfdas={flexOrder}
            style={{
                ...(height !== undefined && { height }),
                ...(flexOrder !== undefined && { order: flexOrder }),
            }}
        >
            {children}
        </div>
    );
};

const TileItem: React.FC<{ className?: string; children: React.ReactNode }> = ({ children, className }) => (
    <div className={clsx(css.item, className && className)}>{children}</div>
);

const TileHeader: React.FC<HeaderProps> = ({
    children,
    counter,
    more,
    divider,
    beforeContent,
    modalCloseButtonMobile,
    isMobileFullscreen,
    className,
}) => {
    const { isMobile } = useContext(WindowSizeContext);

    // Div children will occur for skeletons, in this case we should not wrap them in Typography
    const isNonTextChild = React.Children.count(children) !== 1 || typeof children !== 'string';

    return (
        <div
            className={clsx(
                isMobileFullscreen && isMobile ? css.modalHeader : css.header,
                divider && css.divider,
                className && className,
            )}
        >
            {isMobile && modalCloseButtonMobile}
            <div className={css.headingWithCounter}>
                {beforeContent}
                <Typography
                    style="HeadMedSemibold"
                    ellipsis={!isNonTextChild && children.length > 20}
                    className={css.heading}
                    tag={isNonTextChild ? 'div' : 'p'}
                >
                    {children}
                </Typography>
                {counter !== 0 && <Typography color="gray2">{counter}</Typography>}
            </div>
            {more && <div className={css.moreAction}>{more}</div>}
        </div>
    );
};

Tile.Item = TileItem;

Tile.Header = TileHeader;

export default Tile;
